import * as RadixTabs from '@radix-ui/react-tabs';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { tabItemClass, tabCountClass } from './tabStyles';

/**
 * Tabs — the ONE underline "changes what you are looking at" primitive.
 * Underline = navigation. There is deliberately no pill/boxed variant here;
 * filtering controls belong in Toolbar (ToolbarChip).
 *
 * The class recipes live in ./tabStyles so SubTabBar (router <Link>-based,
 * second-level nav) renders the exact same language without duplicating it.
 */

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
  icon?: LucideIcon;
  disabled?: boolean;
  /** Attention accent: warn-colored label + indicator for alert-bearing tabs. */
  amber?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** primary = case-level nav (36px items). secondary = in-page (32px items).
   *  Same underline language, height differs only. */
  level?: 'primary' | 'secondary';
  /** Aligns the first label's text with the 24px content gutter below it
   *  (item px = page gutter, nav px = 0). */
  inset?: boolean;
  ariaLabel: string;
  className?: string;
}

export default function Tabs({
  items,
  value,
  onChange,
  level = 'primary',
  inset = false,
  ariaLabel,
  className,
}: TabsProps) {
  return (
    <RadixTabs.Root value={value} onValueChange={onChange} activationMode="automatic">
      <div className={cn('border-b border-border', className)}>
        <RadixTabs.List aria-label={ariaLabel} className="-mb-px flex overflow-x-auto">
          {items.map((item) => {
            const active = item.id === value;
            const Icon = item.icon;
            return (
              <RadixTabs.Trigger
                key={item.id}
                value={item.id}
                disabled={item.disabled}
                className={tabItemClass({ active, amber: item.amber, inset, level })}
              >
                {Icon && <Icon size={16} className="shrink-0" aria-hidden="true" />}
                <span>{item.label}</span>
                {item.count !== undefined && (
                  <span className={tabCountClass({ active, amber: item.amber })}>{item.count}</span>
                )}
              </RadixTabs.Trigger>
            );
          })}
        </RadixTabs.List>
      </div>
    </RadixTabs.Root>
  );
}
